import React, { createContext, useState, useContext, useMemo } from 'react'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { getUser, verifyRetry } from 'api/auth'
import {
  login as loginApi,
  register as registerApi,
  logout as logoutApi,
  AUTH,
} from 'api/auth'

import { User } from 'shared'
import { Typography, Box } from '@mui/material'
import {
  addUserInfoToStorage,
  getOnboardingFromStorage,
  getUserInfoFromStorage,
  removeUserInfoFromStorage,
} from 'utils/helpers'

interface AuthContextProps {
  user: User | null
  userId: string
  isUserLoading: boolean
  isUserFetching: boolean
  login: (data: AUTH) => void
  logout: () => void
  resetUser: () => void
  register: (data: AUTH) => void
  loginLoading: boolean
  registerLoading: boolean
  isLoggingOut: boolean
  isProfileFulfilled: boolean
}

const AuthContext = createContext<AuthContextProps>({
  user: null,
  userId: '',
  isUserLoading: true,
  login: () => null,
  logout: () => null,
  resetUser: () => null,
  register: () => null,
  loginLoading: false,
  registerLoading: false,
  isLoggingOut: false,
  isUserFetching: false,
  isProfileFulfilled: false,
})

const ResendLinkToast = ({ email }: { email: string }) => {
  const verifyRetryMutation = useMutation({
    mutationFn: verifyRetry,
    onSuccess: () => {
      toast.success('Please, check your email')
    },
  })

  const onClick = () => {
    verifyRetryMutation.mutate({ email })
  }

  return (
    <Typography>
      Click{' '}
      <Box
        component="span"
        sx={{ textDecoration: 'underline' }}
        onClick={onClick}
      >
        here
      </Box>{' '}
      to resend a verification link
    </Typography>
  )
}

export const POSTPONED_FORM_ATTRIBUTES = {
  product_interests: ['content_types'],
  beauty_preferences: [
    'depth',
    'undertone',
    'skin_type',
    'skin_characteristics',
    'eye_color',
    'hair_color',
    'hair_type',
    'hair_thickness',
    'hair_characteristics',
  ],
  sizing_preferences: [
    'pants_size_w',
    'pants_size_m',
    'tops_size',
    'suits_size',
    'shoe_size_new',
    'dress_size',
    'foot_width',
    'pants_type',
    'ear_piercings',
    'ear_piercing_types',
    'body_piercings',
    'height',
    'weight',
    'body_type',
    'bra_band_size',
    'bra_cup_size',
    'shapewear_size',
  ],
}

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const userIdFromStorage = getUserInfoFromStorage()
  const queryClient = useQueryClient()

  const [userId, setUserId] = useState<string>(userIdFromStorage)

  const {
    data: user = null,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['user'],
    queryFn: getUser,
    enabled: !!userId,
  })

  const isProfileFulfilled = useMemo(() => {
    if (!user?.profile) return false
    const keys = {
      product_interests: false,
      beauty_preferences: false,
      sizing_preferences: false,
    }

    for (const attr of user.profile.attributes) {
      if (POSTPONED_FORM_ATTRIBUTES.product_interests.includes(attr.type))
        keys.product_interests = true
      if (POSTPONED_FORM_ATTRIBUTES.beauty_preferences.includes(attr.type))
        keys.beauty_preferences = true
      if (POSTPONED_FORM_ATTRIBUTES.sizing_preferences.includes(attr.type))
        keys.sizing_preferences = true
    }

    return Object.values(keys).every((value) => value)
  }, [user])

  const loginMutation = useMutation({
    mutationFn: loginApi,
    onSuccess: (data) => {
      const userId = data?.userId
      setUserId(userId)
      addUserInfoToStorage(userId)
      queryClient.invalidateQueries(['user'])
    },
    onError: (error: AxiosError<{ error: string; status: number }>, data) => {
      if (error?.response?.data?.error === 'User is not confirmed.') {
        toast.info(<ResendLinkToast email={data.email} />)
      }
    },
  })
  const registerMutation = useMutation({
    mutationFn: registerApi,
    onSuccess: () => {
      toast.success('Please, check your email')
    },
  })

  const logoutMutation = useMutation({
    mutationFn: logoutApi,
  })

  const login = (data: AUTH) => loginMutation.mutate(data)

  const register = (data: AUTH) => registerMutation.mutate(data)

  const logout = () => {
    const onboarding = getOnboardingFromStorage()
    localStorage.setItem(
      'onboarding',
      JSON.stringify({
        ...onboarding,
        skip: false,
      })
    )
    setUserId('')
    removeUserInfoFromStorage()
    logoutMutation.mutate()
    queryClient.setQueryData(['user'], null)
  }

  const resetUser = () => {
    const onboarding = getOnboardingFromStorage()
    localStorage.setItem(
      'onboarding',
      JSON.stringify({
        ...onboarding,
        skip: false,
      })
    )
    setUserId('')
    removeUserInfoFromStorage()
  }

  return (
    <AuthContext.Provider
      value={{
        user: user || null,
        userId,
        isUserLoading: isLoading,
        isUserFetching: isFetching,
        login,
        logout,
        register,
        resetUser,
        isLoggingOut: logoutMutation.isLoading,
        loginLoading: loginMutation?.isLoading,
        registerLoading: registerMutation?.isLoading,
        isProfileFulfilled,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)

export default AuthProvider
